import React, { useEffect, useRef } from 'react';
import { TourStep } from '../hooks/useTour';
import { createPortal } from 'react-dom';

interface TourTooltipProps {
    step: TourStep;
    currentStepIndex: number;
    totalSteps: number;
    onNext: () => void;
    onPrevious: () => void;
    onSkip: () => void;
}

export function TourTooltip({
    step,
    currentStepIndex,
    totalSteps,
    onNext,
    onPrevious,
    onSkip,
}: TourTooltipProps): JSX.Element | null {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        const targetElement = document.querySelector(`[data-tour="${step.target}"]`);

        if (targetElement && tooltipRef.current) {
            const targetRect = targetElement.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            let top = 0;
            let left = 0;

            const placement = step.placement || 'right';
            const offset = 20;

            switch (placement) {
                case 'right':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
                    left = targetRect.right + offset;
                    break;
                case 'left':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
                    left = targetRect.left - tooltipRect.width - offset;
                    break;
                case 'top':
                    top = targetRect.top - tooltipRect.height - offset;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
                    break;
                case 'bottom':
                    top = targetRect.bottom + offset;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
                    break;
            }

            const padding = 10;
            top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));
            left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));

            setPosition({ top, left });

            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add highlight class to target
            targetElement.classList.add('tour-highlight');

            return () => {
                targetElement.classList.remove('tour-highlight');
            };
        }
    }, [step.target, step.placement]);

    if (!position) {
        return (
            <div
                ref={tooltipRef}
                className="fixed invisible max-w-sm"
            >
                <div className="bg-white p-5 rounded-lg shadow-2xl border-2 border-emerald-300">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-sm font-bold mb-2 text-emerald-600">
                                {step.title}
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                {step.content}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return createPortal(
        <>
            {/* Overlay */}
            <div
                className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 z-[9998] pointer-events-auto"
                onClick={onSkip}
            />

            {/* Tooltip */}
            <div
                className="fixed z-[9999] pointer-events-auto animate-fadeIn"
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                }}
            >
                <div
                    ref={tooltipRef}
                    className="bg-white p-5 rounded-lg shadow-2xl border-2 border-emerald-300 max-w-sm"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-bold text-emerald-600">
                                    {step.title}
                                </h3>
                                <span className="text-xs text-slate-500 font-semibold">
                                    {currentStepIndex + 1} of {totalSteps}
                                </span>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                {step.content}
                            </p>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <button
                                onClick={onSkip}
                                className="text-sm text-slate-500 hover:text-slate-700 font-medium"
                            >
                                Skip Tour
                            </button>

                            <div className="flex gap-2">
                                {currentStepIndex > 0 && (
                                    <button
                                        onClick={onPrevious}
                                        className="px-3 py-1.5 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                                    >
                                        Previous
                                    </button>
                                )}
                                <button
                                    onClick={onNext}
                                    className="px-3 py-1.5 text-sm font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                                >
                                    {currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in;
        }

        [data-tour] {
          position: relative;
        }

        [data-tour].tour-highlight {
          z-index: 9997 !important;
          position: relative !important;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.9), 0 0 25px 5px rgba(16, 185, 129, 0.6) !important;
          border-radius: 0.5rem !important;
          background-color: inherit;
        }
      `}</style>
        </>,
        document.body
    );
}
